import { useEffect, useRef, useState } from "react";
import {
    IoChevronDown,
    IoCloudUploadOutline,
    IoClose,
    IoPaperPlaneOutline,
    IoTimeOutline,
} from "react-icons/io5";
import { useNavigate, useLocation } from "react-router-dom";
import { whtsappIcon } from "../../asset";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import {
    fetchWhatsappSendList,
    sendWhatsappTemplate,
    sendWhatsappBroadcastBulk,
} from "../../features/template/TemplateService";
import WhatsappPreview from "../template/WhtsappPreview";
import { toast } from "sonner";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import { getAuthUser, getDataUserId } from "../../util/manageAuth";
import { uploadFile } from "../../util/imageUpload";
import ScheduleTimeModal from "../send-message/Scheduletimemodal";
import { fetchWalletDetails } from "../../features/wallet/WalletServices";

/**
 * single    → one phone number        → sendWhatsappTemplate
 * bulk      → Excel upload only       → sendWhatsappBroadcastBulk (file)
 * broadcast → selected customers only → sendWhatsappBroadcastBulk (mobileNumbers)
 * Schedule  → same API + scheduledAt ("2026-09-29T00:14") + timeZone
 */
type SendMode = "single" | "bulk" | "broadcast";

interface SendMsgComponentProps {
    mode: SendMode;
}

const mapButtonType = (apiType: string): "Quick Reply" | "URL" | "Call to Action" => {
    switch (apiType?.toUpperCase()) {
        case "QUICK_REPLY": return "Quick Reply";
        case "URL": return "URL";
        case "PHONE_NUMBER": return "Call to Action";
        default: return "Quick Reply";
    }
};

const mapButtons = (rawButtons: any[]) =>
    (rawButtons || []).map((btn: any, index: number) => ({
        id: String(index),
        type: mapButtonType(btn.type),
        text: btn.text || "",
        url: btn.url || undefined,
        phoneNumber: btn.phone_number || undefined,
    }));

const hasVariables = (text: string) => /\{\{\d+\}\}/.test(text || "");

const extractVarCount = (text: string) => {
    const matches = text?.match(/\{\{(\d+)\}\}/g) || [];
    const numbers = matches.map(m => Number(m.replace(/[{}]/g, "")));
    return numbers.length ? Math.max(...numbers) : 0;
};

// Name required by /api/broadcasts/bulk — generated, not typed by the user
const autoBroadcastName = (templateName: string) =>
    `${templateName} - ${new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })}`;

// Digits only, e.g. "+91 73511 66989" → "917351166989"
const cleanMobile = (m: any) => String(m ?? "").replace(/\D/g, "");

export default function SendMsgComponentVariables({ mode }: SendMsgComponentProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const userId = getDataUserId();
    const wallet = useSelector((state: RootState) => state.wallet.data);
    const walletBalance = wallet?.balance ?? 0;
    const hasSufficientBalance = walletBalance > 0;

    const isReturningFromGallery = useRef(false);

    const isSingle = mode === "single";
    const isBulk = mode === "bulk";           // Excel only
    const isBroadcast = mode === "broadcast"; // customers only

    const [templates, setTemplates] = useState<any[]>([]);
    const [openTemplate, setOpenTemplate] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

    const [phoneNumber, setPhoneNumber] = useState<string | undefined>();

    const [selectedLanguage, setSelectedLanguage] = useState<string>("en_US");
    const [isLoading, setIsLoading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);

    // ── Media for header preview ──
    const [headerMediaFile, setHeaderMediaFile] = useState<File | null>(null);
    const [headerMediaPreviewUrl, setHeaderMediaPreviewUrl] = useState<string>("");

    const [headerMediaUploadedUrl, setHeaderMediaUploadedUrl] = useState<string | undefined>();
    const [headerMediaUploading, setHeaderMediaUploading] = useState(false);

    // Selected customers from CustomerList picker
    const [selectedCustomerIds, setSelectedCustomerIds] = useState<number[]>([]);
    const [selectedCustomerNames, setSelectedCustomerNames] = useState<string[]>([]);
    const [selectedCustomerMobiles, setSelectedCustomerMobiles] = useState<string[]>([]);

    // Schedule state
    const [showTimePicker, setShowTimePicker] = useState(false);
    const [scheduleTimeLabel, setScheduleTimeLabel] = useState<string>("");
    const [scheduleDate, setScheduleDate] = useState<string>("");
    const [scheduleTime, setScheduleTime] = useState<string>("");
    const [, setScheduleType] = useState<"ONCE" | "DAILY">("ONCE");

    const [headerParams, setHeaderParams] = useState<string[]>([]);
    const [bodyParams, setBodyParams] = useState<string[]>([]);
    const [buttonUrlParams, setButtonUrlParams] = useState<string[]>([]);

    // Determine if the selected template has a media header
    const headerType = selectedTemplate?.headerType?.toUpperCase();
    const hasMediaHeader = ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerType);

    // Scheduling only for bulk / broadcast
    const isScheduled = !isSingle && !!(scheduleDate && scheduleTime);

    const [formState, setFormState] = useState({
        phoneNumber: "",
        selectedTemplate: null as any,
        selectedLanguage: "en_US",
        uploadedFileName: "",
        selectedCustomerIds: [] as number[],
        selectedCustomerNames: [] as string[],
        selectedCustomerMobiles: [] as string[],
        headerMediaPreviewUrl: "",
        headerMediaUploadedUrl: undefined as string | undefined,
    });

    useEffect(() => {
        if (!location.state) return;

        const saved = sessionStorage.getItem("sendMsgForm");
        if (saved) {
            const parsed = JSON.parse(saved);

            // Prevent the "template changed" effect from wiping restored media
            isReturningFromGallery.current = true;

            setFormState(parsed);
            setPhoneNumber(parsed.phoneNumber);
            setSelectedTemplate(parsed.selectedTemplate);
            setSelectedLanguage(parsed.selectedLanguage);
            setSelectedCustomerIds(parsed.selectedCustomerIds || []);
            setSelectedCustomerNames(parsed.selectedCustomerNames || []);
            setSelectedCustomerMobiles(parsed.selectedCustomerMobiles || []);
            setHeaderMediaPreviewUrl(parsed.headerMediaPreviewUrl || "");
            setHeaderMediaUploadedUrl(parsed.headerMediaUploadedUrl || undefined);
        }
    }, []);

    /* ── Switching between Single / Bulk / Broadcast: clear recipients + schedule ── */
    const prevMode = useRef(mode);
    useEffect(() => {
        if (prevMode.current === mode) return;
        prevMode.current = mode;

        setPhoneNumber(undefined);
        setUploadedFile(null);
        setSelectedCustomerIds([]);
        setSelectedCustomerNames([]);
        setSelectedCustomerMobiles([]);
        clearSchedule();
        setFormState(prev => ({
            ...prev,
            phoneNumber: "",
            uploadedFileName: "",
            selectedCustomerIds: [],
            selectedCustomerNames: [],
            selectedCustomerMobiles: [],
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    /* ── Revoke blob URL on cleanup to avoid memory leaks ── */
    useEffect(() => {
        return () => {
            if (headerMediaPreviewUrl) URL.revokeObjectURL(headerMediaPreviewUrl);
        };
    }, [headerMediaPreviewUrl]);

    /* ── When template changes, clear the previous media ── */
    useEffect(() => {
        if (!selectedTemplate) return;
        if (isReturningFromGallery.current) {
            isReturningFromGallery.current = false;
            return;
        }
        setHeaderMediaFile(null);
        setHeaderMediaPreviewUrl("");
        setHeaderMediaUploadedUrl(undefined);
        setFormState(prev => ({
            ...prev,
            headerMediaPreviewUrl: "",
            headerMediaUploadedUrl: undefined,
        }));
    }, [selectedTemplate?.templateName]);

    /* ── Listen for customers selected from picker ── */
    useEffect(() => {
        if (location.state?.selectedCustomerIds) {
            const ids = location.state.selectedCustomerIds;
            const names = location.state.selectedCustomerNames || [];
            const mobiles = (location.state.selectedCustomerMobiles || [])
                .map(cleanMobile)
                .filter(Boolean);

            setSelectedCustomerIds(ids);
            setSelectedCustomerNames(names);
            setSelectedCustomerMobiles(mobiles);
            setFormState(prev => ({
                ...prev,
                selectedCustomerIds: ids,
                selectedCustomerNames: names,
                selectedCustomerMobiles: mobiles,
            }));

            // clear state so it doesn't persist on refresh
            navigate(location.pathname, { replace: true });
        }
    }, [location.state]);

    /* ── Listen for gallery media selection ── */
    useEffect(() => {
        if (location.state?.selectedMedia) {
            const media = location.state.selectedMedia;

            isReturningFromGallery.current = true;

            setHeaderMediaPreviewUrl(media.mediaUrl);
            setHeaderMediaFile(null);
            setHeaderMediaUploadedUrl(media.mediaUrl);

            setFormState(prev => ({
                ...prev,
                headerMediaPreviewUrl: media.mediaUrl,
                headerMediaUploadedUrl: media.mediaUrl,
            }));

            setHeaderMediaUploading(false);

            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]);

    useEffect(() => {
        sessionStorage.setItem("sendMsgForm", JSON.stringify(formState));
    }, [formState]);

    /* -------- FETCH TEMPLATE LIST -------- */
    useEffect(() => {
        const loadTemplates = async () => {
            try {
                const result = await dispatch(
                    fetchWhatsappSendList({ page: 0, size: 100 })
                ).unwrap();

                setTemplates(
                    (result?.results || []).filter(
                        (item: any) =>
                            item.status?.toUpperCase() === "APPROVED"
                    )
                );
            } catch (err) {
                console.error(err);
            }
        };

        loadTemplates();
    }, [dispatch]);

    useEffect(() => {
        if (!selectedTemplate) return;
        setHeaderParams([]);
        setBodyParams([]);
        setButtonUrlParams([]);
    }, [selectedTemplate?.templateName]);

    const handleHeaderMediaChange = async (file: File) => {
        if (headerMediaPreviewUrl) URL.revokeObjectURL(headerMediaPreviewUrl);
        setHeaderMediaFile(file);
        setHeaderMediaPreviewUrl(URL.createObjectURL(file));
        setHeaderMediaUploadedUrl(undefined);

        try {
            setHeaderMediaUploading(true);
            const url = await uploadFile(file);
            setHeaderMediaUploadedUrl(url);
            setHeaderMediaPreviewUrl(url);
            setFormState(prev => ({
                ...prev,
                headerMediaUploadedUrl: url,
                headerMediaPreviewUrl: url,
            }));
        } catch (err: any) {
            toast.error(err.message || "Failed to upload header media");
            setHeaderMediaFile(null);
            setHeaderMediaPreviewUrl("");
        } finally {
            setHeaderMediaUploading(false);
        }
    };

    const clearSchedule = () => {
        setScheduleTimeLabel("");
        setScheduleDate("");
        setScheduleTime("");
        setScheduleType("ONCE");
    };

    const resetForm = () => {
        sessionStorage.removeItem("sendMsgForm");
        setPhoneNumber(undefined);
        setSelectedTemplate(null);
        setSelectedLanguage("en_US");
        setUploadedFile(null);
        setHeaderMediaFile(null);
        setHeaderMediaPreviewUrl("");
        setHeaderMediaUploadedUrl(undefined);
        setSelectedCustomerIds([]);
        setSelectedCustomerNames([]);
        setSelectedCustomerMobiles([]);
        clearSchedule();
    };

    /* ── VALIDATION: returns an error message, or null if OK ── */
    const validate = (): string | null => {
        if (!selectedTemplate) return "Please select a template";
        if (!selectedLanguage) return "Please select a language";
        if (hasMediaHeader && headerMediaUploading) return "Please wait for the media to finish uploading";
        if (hasMediaHeader && !headerMediaUploadedUrl) return "Please upload a header media file";

        // Recipients — one type per mode
        if (isSingle && (!phoneNumber || !isValidPhoneNumber(phoneNumber))) return "Enter a valid phone number";
        if (isBulk && !uploadedFile) return "Please upload an Excel file";
        if (isBroadcast && selectedCustomerMobiles.length === 0) return "Please select customers";

        // Variables are typed in Single / Broadcast (Bulk takes them from the Excel)
        if (!isBulk) {
            const headerVarCount = extractVarCount(selectedTemplate?.headerText || "");
            const bodyVarCount = extractVarCount(selectedTemplate?.bodyText || "");

            for (let i = 0; i < headerVarCount; i++) {
                if (!headerParams[i]?.trim()) return `Please fill Header Variable {{${i + 1}}}`;
            }
            for (let i = 0; i < bodyVarCount; i++) {
                if (!bodyParams[i]?.trim()) return `Please fill Body Variable {{${i + 1}}}`;
            }

            const dynamicUrlButtons = selectedTemplate?.buttons?.filter(
                (btn: any) => btn.type === "URL" && hasVariables(btn.url)
            ) || [];
            for (let i = 0; i < dynamicUrlButtons.length; i++) {
                if (!buttonUrlParams[i]?.trim()) {
                    return `Please fill URL Variable for button "${dynamicUrlButtons[i].text}"`;
                }
            }
        }

        return null;
    };

    /* ── HANDLE SEND ── */
    const handleSend = async () => {
        const error = validate();
        if (error) {
            toast.error(error);
            return;
        }

        const variables = {
            headerParams: headerParams.filter(Boolean),
            bodyParams: bodyParams.filter(Boolean),
            buttonUrlParams: buttonUrlParams.filter(Boolean),
        };

        try {
            setIsLoading(true);

            if (isSingle) {
                // SINGLE → one number
                await dispatch(
                    sendWhatsappTemplate({
                        to: phoneNumber!.replace("+", ""),
                        templateName: selectedTemplate.templateName,
                        language: selectedLanguage,
                        headerMediaId: headerMediaUploadedUrl,
                        headerType,
                        ...variables,
                    })
                ).unwrap();
            } else {
                // BULK (Excel) + BROADCAST (customers) → same API, now or scheduled
                await dispatch(
                    sendWhatsappBroadcastBulk({
                        name: autoBroadcastName(selectedTemplate.templateName),
                        templateName: selectedTemplate.templateName,
                        language: selectedLanguage,
                        headerMediaId: hasMediaHeader ? headerMediaUploadedUrl : undefined,
                        headerType: hasMediaHeader ? headerType : undefined,

                        // Recipients
                        ...(isBulk
                            ? { file: uploadedFile }
                            : { mobileNumbers: selectedCustomerMobiles, ...variables }),

                        // Only when scheduled → "2026-09-29T00:14"
                        scheduledAt: isScheduled
                            ? `${scheduleDate}T${scheduleTime.slice(0, 5)}`
                            : undefined,
                        timeZone: isScheduled ? "Asia/Kolkata" : undefined,
                    })
                ).unwrap();
            }

            if (userId) {
                dispatch(fetchWalletDetails(Number(userId)));
            }

            toast.success(isScheduled ? "Message scheduled successfully!" : "Message sent successfully!");
            resetForm();
        } catch (error: any) {
            const errMsg =
                typeof error === "string"
                    ? error
                    : error?.message || error?.field || "Failed to send message";
            toast.error(errMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const removeUploadedFile = () => {
        setUploadedFile(null);
        setFormState(prev => ({ ...prev, uploadedFileName: "" }));
    };

    // Style for the "Send now" / "Schedule" option boxes
    const optionBox = (active: boolean) =>
        `flex items-start gap-3 text-left border-2 rounded-2xl p-4 transition disabled:opacity-50 disabled:cursor-not-allowed ${active ? "border-brand-main bg-brand-main/5" : "border-gray-200 hover:border-gray-400"
        }`;

    return (
        <div className="flex md:flex-row flex-col gap-2 bg-white rounded-4xl p-6">
            <div className="md:w-3/5 w-full flex flex-col gap-6 border rounded-4xl shadow-lg p-6">

                {/* ── Top bar ── */}
                <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-gray-800">Message Details</span>
                </div>

                {/* ── Sender ── */}
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-bold">Sender's Whatsapp Number</label>
                    <div className="flex items-center justify-between border rounded-xl px-4 py-3 cursor-pointer hover:border-gray-400 transition">
                        <div className="flex items-center gap-3">
                            <img src={whtsappIcon} className="w-8 h-8" />
                            <div className="flex flex-col">
                                <span className="font-semibold text-gray-800">Empedance.com</span>
                                <span className="text-sm text-gray-500">
                                    {(getAuthUser()?.mobile || "Not Connected").replace("+", "")}
                                    <span className="text-green-600 font-semibold ml-2">• CONNECTED</span>
                                </span>
                            </div>
                        </div>
                        <IoChevronDown className="text-gray-500" />
                    </div>
                </div>

                {/* Template Dropdown */}
                <div className="flex flex-col gap-2 relative">
                    <label className="text-sm font-bold">Template</label>
                    <div
                        onClick={() => setOpenTemplate(!openTemplate)}
                        className="flex items-center justify-between border rounded-xl px-4 py-3 cursor-pointer hover:border-gray-400 transition"
                    >
                        <span className="text-gray-700">
                            {selectedTemplate?.templateName || "Select Template"}
                        </span>
                        <IoChevronDown />
                    </div>
                    {openTemplate && (
                        <div className="absolute top-full mt-2 w-full bg-white border rounded-xl shadow-lg z-20 max-h-60 overflow-y-auto">
                            {templates.map((item: any) => (
                                <div
                                    key={item.id}
                                    onClick={() => {
                                        const headerComponent = item.components?.find((c: any) => c.type === "HEADER");
                                        const bodyComponent = item.components?.find((c: any) => c.type === "BODY");
                                        const footerComponent = item.components?.find((c: any) => c.type === "FOOTER");
                                        const buttonsComponent = item.components?.find((c: any) => c.type === "BUTTONS");

                                        const selected = {
                                            templateName: item.name,
                                            headerType: headerComponent?.format || null,
                                            headerText: headerComponent?.text || null,
                                            headerImageUrl: headerComponent?.example?.header_handle?.[0] || null,
                                            bodyText: bodyComponent?.text || null,
                                            footerText: footerComponent?.text || null,
                                            buttons: mapButtons(buttonsComponent?.buttons),
                                        };

                                        setSelectedTemplate(selected);

                                        setFormState(prev => ({
                                            ...prev,
                                            selectedTemplate: selected
                                        }));

                                        setOpenTemplate(false);
                                    }}
                                    className="px-4 py-3 hover:bg-gray-100 cursor-pointer text-sm font-semibold"
                                >
                                    {item.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Header Media Upload (only shown when template has IMAGE/VIDEO/DOCUMENT header) ── */}
                {hasMediaHeader && !(headerMediaFile || headerMediaPreviewUrl) && (
                    <>
                        {/* Choose from Gallery */}
                        <button
                            onClick={() => {
                                navigate("/admin/gallery", {
                                    state: {
                                        mode: "select",
                                        returnTo: `/admin/variables-send-message/${mode}`
                                    }
                                });
                            }}
                            className="w-full px-4 py-2 bg-brand-blue-gradient text-white border rounded-xl text-sm font-semibold hover:bg-gray-50 transition"
                        >
                            Choose from Gallery
                        </button>

                        <Divider />

                        {/* Upload */}
                        <div className="border-2 border-dashed rounded-2xl p-5 flex flex-col items-center gap-2 cursor-pointer relative">
                            <input
                                type="file"
                                accept={
                                    headerType === "IMAGE" ? "image/*"
                                        : headerType === "VIDEO" ? "video/*"
                                            : ".pdf,.doc,.docx"
                                }
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) handleHeaderMediaChange(f);
                                }}
                            />
                            <IoCloudUploadOutline className="text-3xl text-gray-400" />
                            <span className="text-sm text-gray-400">
                                Click to upload {headerType.toLowerCase()}
                            </span>
                        </div>
                    </>
                )}

                {hasMediaHeader && (headerMediaFile || headerMediaPreviewUrl) && headerMediaUploading && (
                    <div className="flex items-center gap-3 border rounded-xl p-3 bg-gray-50">
                        <div className="w-5 h-5 border-2 border-brand-main border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-gray-600 font-semibold">Uploading media...</span>
                    </div>
                )}

                {/* Header variable inputs (not for Bulk — values come from Excel) */}
                {!isBulk && selectedTemplate?.headerType === "TEXT" && hasVariables(selectedTemplate?.headerText) && (
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-bold">Header Variables</label>
                        {Array.from({ length: extractVarCount(selectedTemplate.headerText) }, (_, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 w-10">{`{{${i + 1}}}`}</span>
                                <input
                                    type="text"
                                    placeholder={`Value for {{${i + 1}}}`}
                                    value={headerParams[i] || ""}
                                    onChange={(e) => {
                                        const updated = [...headerParams];
                                        updated[i] = e.target.value;
                                        setHeaderParams(updated);
                                    }}
                                    className="flex-1 border border-gray-300 rounded-xl p-2 text-sm focus:outline-none focus:border-brand-main"
                                />
                            </div>
                        ))}
                    </div>
                )}

                {/* ── SINGLE: one phone number ── */}
                {isSingle && (
                    <>
                        <label className="text-sm font-bold">Recipient WhatsApp Number</label>
                        <div className="border rounded-xl px-4 py-2 focus-within:border-brand-main">
                            <PhoneInput
                                defaultCountry="IN"
                                limitMaxLength
                                value={phoneNumber}
                                onChange={(val) => {
                                    setPhoneNumber(val);
                                    setFormState(prev => ({ ...prev, phoneNumber: val || "" }));
                                }}
                                className="phone-input focus:outline-none"
                            />
                        </div>
                    </>
                )}

                {/* ── BROADCAST: select customers only ── */}
                {isBroadcast && (
                    <div className="flex flex-col gap-3">
                        <label className="text-sm font-bold">Recipients Whatsapp Number</label>
                        <button
                            onClick={() =>
                                navigate("/admin/customers?mode=select", {
                                    state: { returnTo: `/admin/variables-send-message/${mode}` },
                                })
                            }
                            disabled={headerMediaUploading}
                            className="bg-brand-main text-white rounded-xl py-3 font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Select Customers
                            {selectedCustomerIds.length > 0 && (
                                <span className="ml-2 bg-white text-brand-main text-xs font-bold px-2 py-0.5 rounded-full">
                                    {selectedCustomerIds.length}
                                </span>
                            )}
                        </button>
                    </div>
                )}

                {/* ── BULK: Excel upload only ── */}
                {isBulk && (
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-bold">Recipients Whatsapp Number (Upload file)</label>

                        <div className="border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer hover:border-brand-main transition relative">
                            <input
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                onChange={(e) => {
                                    const file = e.target.files?.[0] ?? null;
                                    setUploadedFile(file);
                                    if (file) {
                                        setFormState(prev => ({ ...prev, uploadedFileName: file.name }));
                                    }
                                    e.target.value = ""; // allow picking the same file again
                                }}
                            />
                            <div className="flex flex-col items-center gap-2 text-gray-500">
                                <IoCloudUploadOutline className="text-3xl" />
                                {uploadedFile ? (
                                    <span className="font-semibold text-brand-main">{uploadedFile.name}</span>
                                ) : formState.uploadedFileName ? (
                                    <span className="text-gray-400 text-sm">
                                        Previously selected: {formState.uploadedFileName}
                                    </span>
                                ) : (
                                    <>
                                        <span className="font-semibold">Click to upload or drag and drop</span>
                                        <span className="text-sm">XLSX, XLS, CSV files</span>
                                    </>
                                )}
                            </div>
                        </div>

                        {uploadedFile && (
                            <button
                                onClick={removeUploadedFile}
                                className="self-end text-xs text-brand-main font-semibold hover:underline flex items-center gap-1"
                            >
                                <IoClose size={14} />
                                Remove file
                            </button>
                        )}

                        {(extractVarCount(selectedTemplate?.headerText || "") > 0 ||
                            extractVarCount(selectedTemplate?.bodyText || "") > 0) && (
                                <p className="text-xs text-gray-500">
                                    This template has variables. Their values will be taken from the
                                    columns of your uploaded file, row by row.
                                </p>
                            )}
                    </div>
                )}

                {/* Body variable inputs (not for Bulk) */}
                {!isBulk && hasVariables(selectedTemplate?.bodyText) && (
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-bold">Body Variables</label>
                        {Array.from({ length: extractVarCount(selectedTemplate.bodyText) }, (_, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 w-10">{`{{${i + 1}}}`}</span>
                                <input
                                    type="text"
                                    placeholder={`Value for {{${i + 1}}}`}
                                    value={bodyParams[i] || ""}
                                    onChange={(e) => {
                                        const updated = [...bodyParams];
                                        updated[i] = e.target.value;
                                        setBodyParams(updated);
                                    }}
                                    className="flex-1 border border-gray-300 rounded-xl p-2 text-sm focus:outline-none focus:border-brand-main"
                                />
                            </div>
                        ))}
                    </div>
                )}

                {/* Button URL variable inputs (not for Bulk) */}
                {!isBulk && selectedTemplate?.buttons?.some((btn: any) => btn.type === "URL" && hasVariables(btn.url)) && (
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-bold">Button URL Variables</label>
                        {selectedTemplate.buttons
                            .filter((btn: any) => btn.type === "URL" && hasVariables(btn.url))
                            .map((btn: any, i: number) => (
                                <div key={i} className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500 w-20 truncate">{btn.text}</span>
                                    <input
                                        type="text"
                                        placeholder="Value for {{1}}"
                                        value={buttonUrlParams[i] || ""}
                                        onChange={(e) => {
                                            const updated = [...buttonUrlParams];
                                            updated[i] = e.target.value;
                                            setButtonUrlParams(updated);
                                        }}
                                        className="flex-1 border border-gray-300 rounded-xl p-2 text-sm focus:outline-none focus:border-brand-main"
                                    />
                                </div>
                            ))}
                    </div>
                )}

                {/* ── When to send: Send now / Schedule (Bulk + Broadcast) ── */}
                {!isSingle && (
                    <div className="flex flex-col gap-4">
                        <label className="text-sm font-bold">When to send</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={clearSchedule}
                                disabled={isLoading}
                                className={optionBox(!isScheduled)}
                            >
                                <IoPaperPlaneOutline className="text-xl mt-0.5 text-brand-main flex-shrink-0" />
                                <span className="flex flex-col">
                                    <span className="font-semibold text-gray-800">Send now</span>
                                    <span className="text-xs text-gray-500">Message goes out immediately</span>
                                </span>
                            </button>

                            <button
                                type="button"
                                onClick={() => setShowTimePicker(true)}
                                disabled={isLoading || headerMediaUploading}
                                className={optionBox(isScheduled)}
                            >
                                <IoTimeOutline className="text-xl mt-0.5 text-brand-main flex-shrink-0" />
                                <span className="flex flex-col min-w-0">
                                    <span className="font-semibold text-gray-800">Schedule</span>
                                    <span className="text-xs text-gray-500 truncate">
                                        {isScheduled ? `${scheduleTimeLabel} · Change` : "Pick a date and time"}
                                    </span>
                                </span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Language */}
                <div className="w-48">
                    <Dropdown
                        label="Language"
                        placeholder="Select"
                        value={selectedLanguage}
                        onChange={setSelectedLanguage}
                        options={[{ label: "English", value: "en_US" }]}
                    />
                </div>

                {/* Send Button */}
                <div className="flex justify-end">
                    <div className="relative group">
                        <button
                            onClick={handleSend}
                            disabled={isLoading || !hasSufficientBalance}
                            className="bg-brand-main text-white rounded-xl px-8 py-3 font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading
                                ? isScheduled ? "Scheduling..." : "Sending..."
                                : isScheduled ? "Schedule Message" : "Send Message"}
                        </button>

                        {!hasSufficientBalance && (
                            <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-50">
                                <div className="rounded-md bg-black text-white text-xs px-2 py-1 whitespace-nowrap shadow-lg">
                                    Insufficient wallet balance
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="md:w-2/5 w-full">
                <WhatsappPreview
                    templateData={selectedTemplate || {}}
                    buttons={selectedTemplate?.buttons || []}
                    mediaPreviewUrl={headerMediaPreviewUrl || undefined}
                    mediaFile={headerMediaFile}
                    headerParams={headerParams}
                    bodyParams={bodyParams}
                    headerMediaUploading={headerMediaUploading}
                />
            </div>

            {/* Schedule Time Picker Modal */}
            {showTimePicker && (
                <ScheduleTimeModal
                    onConfirm={(label, date, time, type) => {
                        setScheduleTimeLabel(label);
                        setScheduleDate(date);
                        setScheduleTime(time);
                        setScheduleType(type);
                        setShowTimePicker(false);
                    }}
                    onClose={() => setShowTimePicker(false)}
                />
            )}
        </div>
    );
}

function Divider() {
    return (
        <div className="flex items-center gap-3 text-gray-400 text-sm">
            <div className="flex-1 border-t" />OR<div className="flex-1 border-t" />
        </div>
    );
}

function Dropdown({
    label,
    placeholder,
    value,
    onChange,
    options = [],
}: {
    label: string;
    placeholder: string;
    value?: string;
    onChange?: (value: string) => void;
    options?: { label: string; value: string }[];
}) {
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption = options.find((opt) => opt.value === value);

    return (
        <div className="flex flex-col gap-2 relative">
            <label className="text-sm font-bold">{label}</label>
            <div
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between border rounded-xl px-4 py-3 cursor-pointer hover:border-gray-400 transition"
            >
                <span className={selectedOption ? "text-gray-700" : "text-gray-500"}>
                    {selectedOption?.label || placeholder}
                </span>
                <IoChevronDown />
            </div>
            {isOpen && options.length > 0 && (
                <div className="absolute top-full mt-2 w-full bg-white border rounded-xl shadow-lg z-20 max-h-60 overflow-y-auto">
                    {options.map((option) => (
                        <div
                            key={option.value}
                            onClick={() => { onChange?.(option.value); setIsOpen(false); }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer text-sm font-semibold"
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}